import type {
  IEntry} from '@app/domain/instrument/instrument.interface';
import {
  QuestionTypeId,
} from '@app/domain/instrument/instrument.interface'
import type { Question } from '@app/domain/instrument/types'
import type {
  IDataInstrument,
  IInstrumentView,
  IScreen,
  IScreenQuestion} from '@app/domain/visit/visit.interface';
import {
  VisitUseCase,
} from '@app/domain/visit/visit.interface'
import { hasContent, stripCharactersFromPhoneNumber } from '@app/lib/string'
import {
  emailValidator,
  stringIsInteger,
  futureDateValidator,
} from '@app/lib/validators'
import type { QuestionsConfig } from '@app/state/config/interfaces'
import * as Helpers from '@app/state/intake/helpers'
import {
  emailValidatorText,
  invalidDateText,
  invalidIntegerText,
  phoneValidatorText,
  invalidYearText,
} from '@app/state/patients/messages'

import {
  QuestionValidationType
} from './form.interface'

import type {
  QuestionValidation,
  UnitRecord} from './form.interface';

export enum FormInputType {
  Text = 'text',
  Date = 'date',
  Binary = 'binary',
  Radio = 'radio',
  Checkbox = 'checkbox',
  Scale = 'scale',
  Select = 'select',
}

export const parseQuestionValue = (
  question: Question,
  questionValue: string,
) => {
  if (questionValue) {
    if (question.inputType === 'checkbox') {
      return questionValue
        .split('|')
        .map((selection) =>
          question.options.find((option) => option.value === selection),
        )
        .filter((it) => it !== undefined)
        .sort(
          (a, b) => question.options.indexOf(a) - question.options.indexOf(b),
        )
        .map((it) => it.label)
        .join(', ')
    } else if (question.inputType === QuestionTypeId.NumericWithUnits) {
      const val = parseFormattedUnitResponseString(questionValue)
      return `${val.value} ${val.unit}`
    }

    if (question.options?.length > 0) {
      return question.options.find((option) => option.value === questionValue)
        ?.label
    }
    return questionValue
  }
  return null
}

export const sortQuestions = (questions: IScreenQuestion[]) => {
  return questions.sort(
    (a: IScreenQuestion, b: IScreenQuestion) => a.sortIndex - b.sortIndex,
  )
}

export const sortScreens = (screens: IScreen[]) => {
  return screens.sort((a: IScreen, b: IScreen) => a.position - b.position)
}

export const createSyntheticClinicView = (views?: IInstrumentView[]) => {
  const sortedScreens = sortScreens(
    views?.find((v) => v.useCase === VisitUseCase.Intake)?.screens ?? [],
  )
  const questions = sortedScreens
    .reduce((allQuestions: IScreenQuestion[], screen: IScreen) => {
      const sortedQuestions = sortQuestions(screen.questions)
      return [...allQuestions, ...sortedQuestions]
    }, [])
    .map((question: IScreenQuestion, sortIndex: number) => ({
      ...question,
      sortIndex,
    }))

  return {
    id: 'synthetic_view',
    useCase: VisitUseCase.Clinic,
    screens: [
      {
        id: 'synthetic_screen',
        position: 0,
        questions,
      },
    ],
  }
}

export const getOrderedQuestions = (currentInstrument: IDataInstrument) => {
  const questions: Question[] = []

  const clinicView =
    currentInstrument.views?.find((v) => v.useCase === VisitUseCase.Clinic) ??
    createSyntheticClinicView(currentInstrument.views)

  if (currentInstrument.views.length > 0) {
    clinicView.screens.forEach((screen: IScreen) => {
      screen.questions.forEach((question) => {
        questions.push(currentInstrument.questions[question.questionId])
      })
    })
  } else {
    for (const key in currentInstrument.questions) {
      questions.push(currentInstrument.questions[key])
    }
  }
  return questions
}

export const getDisabledQuestions = (
  currentInstrument: IDataInstrument,
  entry: IEntry,
  responses?: Record<string, string>,
) => {
  //if we want to use current responses for the disable questions logic, replace the entry responses for current responses
  if (responses) {
    entry = {
      ...entry,
      responses: responses,
    }
  }

  const view = currentInstrument.views.filter(
    (v: IInstrumentView) => v.useCase === 'intake',
  )

  const responsesLookupTable = {}
  if (currentInstrument.questions) {
    Object.keys(currentInstrument.questions).map((qid) => {
      const question = currentInstrument.questions[qid]
      responsesLookupTable[question.id] = entry?.responses[question.id]
    })
  }

  const disabledQuestions: Question[] = []
  if (view.length > 0) {
    view[0].screens.forEach((screen: IScreen) => {
      screen.questions.forEach((question) => {
        if (question.displayLogic && question.displayLogic !== '') {
          const shouldDisplay = Helpers.evaluateConfiguredLogic(
            question.displayLogic,
            {
              instrumentResponses: {
                [currentInstrument.id]: responsesLookupTable,
              },
            },
          )
          if (!shouldDisplay) {
            disabledQuestions.push(
              currentInstrument.questions[question.questionId],
            )
          }
        }
      })
    })
  }
  return disabledQuestions
}

export type DateValidator = (
  value: Date | string,
  currentDate: Date,
  questionsConfig?: QuestionsConfig,
) => string

export const dateQuestionValidator = (
  questionValidation: QuestionValidation,
): DateValidator => {
  switch (questionValidation.type) {
    case QuestionValidationType.DisallowDateInFuture:
      return (date: Date | string, currentDate: Date) => {
        const dateValidator = futureDateValidator(currentDate)
        if (date instanceof Date) {
          return dateValidator(date)
        } else if (typeof date === 'string' && date.trim() !== '') {
          const [year, month, day] = date.split('-').map(Number)
          const convertedDate = new Date(year, month - 1, day)

          if (!isNaN(convertedDate.getTime())) {
            return dateValidator(convertedDate)
          } else {
            return invalidDateText
          }
        } else {
          return invalidDateText
        }
      }
    default:
      return null
  }
}

export type StringValidator = (
  value: string,
  questionsConfig?: QuestionsConfig,
) => string

type StringValidators = Extract<
  QuestionValidationType,
  QuestionValidationType.ValidateEmail
>

type StringValidationTable = {
  [key in StringValidators]: (input: string, config?: QuestionsConfig) => string
}

type NumericValidators = Extract<
  QuestionValidationType,
  | QuestionValidationType.ValidateInteger
  | QuestionValidationType.ValidatePhoneNumber
  | QuestionValidationType.ValidateYear
>

const stringValidationTable: StringValidationTable = {
  [QuestionValidationType.ValidateEmail]: (input: string) =>
    emailValidator(input) ? '' : emailValidatorText,
}

type NumericValidationTable = {
  [key in NumericValidators]: (
    input: string,
    config?: QuestionsConfig,
  ) => string
}

const numericValidationTable: NumericValidationTable = {
  [QuestionValidationType.ValidateInteger]: (input: string) =>
    stringIsInteger(input) ? '' : invalidIntegerText,

  [QuestionValidationType.ValidatePhoneNumber]: (input: string) => {
    const updatedPhoneNumber = stripCharactersFromPhoneNumber(input)
    return stringIsInteger(updatedPhoneNumber) && updatedPhoneNumber.length >= 7
      ? ''
      : phoneValidatorText
  },

  [QuestionValidationType.ValidateYear]: (
    input: string,
    config: QuestionsConfig,
  ) => {
    return stringIsInteger(input) &&
      input.length === 4 &&
      Number(input) > config.yearQuestionLowerLimit
      ? ''
      : `${invalidYearText} Year must be after ${config.yearQuestionLowerLimit}.`
  },
}

export const stringQuestionValidationToValidator = (
  questionValidation: QuestionValidation,
): StringValidator => {
  if (questionValidation.type in stringValidationTable) {
    const validationFunc = stringValidationTable[questionValidation.type]
    return validationFunc ? validationFunc : null
  } else {
    const validationFunc = numericValidationTable[questionValidation.type]
    return validationFunc ? validationFunc : null
  }
}

export const getQuestionValidators = (
  question: Question,
): StringValidator[] => {
  return (
    question?.validation
      ?.map((validation) => stringQuestionValidationToValidator(validation))
      .filter((validator) => validator !== null) ?? []
  )
}

export const getDateQuestionValidators = (
  question: Question,
): DateValidator[] => {
  return (
    question?.validation
      ?.map((validation) => dateQuestionValidator(validation))
      .filter((validator) => validator !== null) ?? []
  )
}

export const getResponseErrorsForStringValidators = (
  validators: StringValidator[],
  response: string,
  questionsConfig?: QuestionsConfig,
): string[] => {
  return validators
    .map((validator) => validator(response, questionsConfig))
    .filter((errorMessage) => errorMessage.trim() !== '')
}

export const getResponseErrorsForDateValidators = (
  validators: DateValidator[],
  response: string,
  currentDate: Date,
  questionsConfig?: QuestionsConfig,
): string[] => {
  return validators
    .map((validator) => validator(response, currentDate, questionsConfig))
    .filter((errorMessage) => errorMessage.trim() !== '')
}

export const isResponseValid = (
  question: Question,
  response?: string,
  questionsConfig?: QuestionsConfig,
): boolean => {
  const responseErrors =
    question.inputType === QuestionTypeId.Date
      ? getResponseErrorsForDateValidators(
          getDateQuestionValidators(question),
          response ?? '',
          new Date(),
          questionsConfig,
        )
      : getResponseErrorsForStringValidators(
          getQuestionValidators(question),
          response ?? '',
          questionsConfig,
        )

  return responseErrors.length === 0
}

export const parseFormattedUnitResponseString = (
  input: string,
): Record<string, string> | null => {
  const record = {}
  if (hasContent(input)) {
    const parts = input.split(', ')

    parts.forEach((part) => {
      const [key, value] = part.split(': ')
      if (key && value) {
        record[key] = value
      }
    })
  }
  return record
}

export const convertUnitRecordToString = (data: UnitRecord | null): string => {
  return `value: ${data?.value ?? ''}, unit: ${data?.unit ?? ''}`
}
